/**
 * WeirDo Markdown is intentionally small and stable. Raw HTML is always
 * escaped; the renderer only emits elements constructed in this module.
 */
export function renderMarkdown(source: string): string {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const blocks: string[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) { index++; continue; }

    if (line.trimStart().startsWith("```")) {
      const code: string[] = [];
      index++;
      while (index < lines.length && !lines[index].trimStart().startsWith("```")) code.push(lines[index++]);
      if (index < lines.length) index++;
      blocks.push(`<pre><code>${escapeHtml(code.join("\n"))}</code></pre>`);
      continue;
    }

    const heading = /^(#{1,6})\s+(.+)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      blocks.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      index++;
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      const quoted: string[] = [];
      while (index < lines.length && /^\s*>\s?/.test(lines[index])) quoted.push(lines[index++].replace(/^\s*>\s?/, ""));
      blocks.push(`<blockquote>${renderMarkdown(quoted.join("\n"))}</blockquote>`);
      continue;
    }

    const list = /^(\s*)([-+*]|\d+\.)\s+(.+)$/.exec(line);
    if (list) {
      const ordered = /\d+\./.test(list[2]);
      const items: string[] = [];
      while (index < lines.length) {
        const item = /^(\s*)([-+*]|\d+\.)\s+(.+)$/.exec(lines[index]);
        if (!item || /\d+\./.test(item[2]) !== ordered) break;
        const checklist = /^\[([ xX])\]\s+(.+)$/.exec(item[3]);
        items.push(checklist
          ? `<li class="markdown-check"><input type="checkbox" disabled ${checklist[1].toLowerCase() === "x" ? "checked" : ""}><span>${renderInline(checklist[2])}</span></li>`
          : `<li>${renderInline(item[3])}</li>`);
        index++;
      }
      blocks.push(`<${ordered ? "ol" : "ul"}>${items.join("")}</${ordered ? "ol" : "ul"}>`);
      continue;
    }

    const paragraph = [line];
    index++;
    while (index < lines.length && lines[index].trim() && !isBlockStart(lines[index])) paragraph.push(lines[index++]);
    blocks.push(`<p>${paragraph.map(renderInline).join("<br>")}</p>`);
  }

  return blocks.join("");
}

function isBlockStart(line: string): boolean {
  return line.trimStart().startsWith("```")
    || /^(#{1,6})\s+/.test(line)
    || /^\s*>\s?/.test(line)
    || /^(\s*)([-+*]|\d+\.)\s+/.test(line);
}

export function renderInline(source: string): string {
  let output = "";
  for (let index = 0; index < source.length;) {
    if (source[index] === "`") {
      const end = source.indexOf("`", index + 1);
      if (end !== -1) { output += `<code>${escapeHtml(source.slice(index + 1, end))}</code>`; index = end + 1; continue; }
    }

    if (source[index] === "[") {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)/.exec(source.slice(index));
      if (link) {
        const href = safeHref(link[2]);
        const external = href && /^(https?:|mailto:)/i.test(href);
        output += href ? `<a href="${escapeHtml(href)}"${external ? ` target="_blank" rel="noopener noreferrer"` : ""}>${renderInline(link[1])}</a>` : renderInline(link[1]);
        index += link[0].length;
        continue;
      }
    }

    const marker = source.slice(index, index + 2);
    if (marker === "**" || marker === "__") {
      const end = source.indexOf(marker, index + 2);
      if (end !== -1) { output += `<strong>${renderInline(source.slice(index + 2, end))}</strong>`; index = end + 2; continue; }
    }

    if (source[index] === "*" || source[index] === "_") {
      const end = source.indexOf(source[index], index + 1);
      if (end > index + 1) { output += `<em>${renderInline(source.slice(index + 1, end))}</em>`; index = end + 1; continue; }
    }

    output += escapeHtml(source[index]);
    index++;
  }
  return output;
}

function safeHref(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.startsWith("/") || trimmed.startsWith("#") || trimmed.startsWith("?")) return trimmed;
  try {
    const url = new URL(trimmed);
    return ["http:", "https:", "mailto:"].includes(url.protocol) ? trimmed : null;
  } catch {
    return null;
  }
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, character => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character]!);
}
